import { Link } from "react-router-dom";
import { Leaf, ArrowRight, ArrowLeft } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";

const SECTIONS = [
  {
    n: 1,
    title: "Make Learning Joyful 😊",
    blocks: [
      { type: "p", text: "Children learn more effectively when they are genuinely interested in what they are learning. Education should create opportunities for curiosity, exploration, discovery, and creativity." },
      { type: "p", text: "Instead of making every lesson focused on memorization, educators can use:" },
      {
        type: "list",
        items: [
          "Stories and real-life examples",
          "Games and educational activities",
          "Group discussions",
          "Experiments and demonstrations",
          "Projects and creative assignments",
          "Technology and interactive resources",
        ],
      },
      { type: "example", text: "Instead of simply asking students to memorize information about plants, allow them to grow a small plant, observe its development, record their observations, and discuss what they discover." },
      { type: "key", text: "Learning should be something students participate in, not something they simply receive." },
    ],
  },
  {
    n: 2,
    title: "Create a Safe and Positive Learning Environment 💛",
    blocks: [
      { type: "p", text: "Students need to feel that they are respected and accepted before they can fully engage in learning." },
      { type: "p", text: "A positive educational environment encourages students to:" },
      {
        type: "list",
        items: [
          "Express their ideas",
          "Ask questions",
          "Make mistakes without humiliation",
          "Share concerns",
          "Work with others",
          "Develop confidence",
        ],
      },
      { type: "p", text: "Teachers and parents can contribute by using encouraging language and avoiding unnecessary comparison between children." },
      { type: "example", text: "Instead of saying, “Why can't you be like your classmate?”, try: “Let's understand what is difficult for you and work on it together.”" },
      { type: "p", text: "A supportive environment can help students see mistakes as opportunities to learn rather than reasons to feel ashamed." },
    ],
  },
  {
    n: 3,
    title: "Encourage Questions and Curiosity 🔎",
    blocks: [
      { type: "p", text: "Questions are an important part of learning. A child who asks “Why?”, “How?”, or “What would happen if…?” is actively exploring the world." },
      { type: "p", text: "Educators can encourage curiosity by:" },
      {
        type: "list",
        items: [
          "Welcoming questions during lessons",
          "Giving students time to investigate",
          "Asking open-ended questions",
          "Allowing different answers and perspectives",
          "Encouraging students to research topics independently",
        ],
      },
      { type: "example", text: "For example, instead of only asking: “What is the answer?” also ask: “How did you reach that answer?” This helps develop reasoning and deeper understanding." },
      { type: "key", label: "Remember", text: "Curiosity can turn education from memorizing information into lifelong learning." },
    ],
  },
  {
    n: 4,
    title: "Celebrate Progress, Not Just Results 🌟",
    blocks: [
      { type: "p", text: "Grades and examination results provide one form of feedback, but they do not describe everything a learner is capable of." },
      { type: "p", text: "Recognize:" },
      {
        type: "list",
        items: [
          "Effort",
          "Improvement",
          "Persistence",
          "Creativity",
          "Cooperation",
          "Responsibility",
          "Problem-solving",
          "Willingness to try again",
        ],
      },
      { type: "example", text: "“You improved your reading this month. Your regular practice is making a difference.” This type of feedback connects achievement with effort and encourages learners to continue developing." },
      { type: "p", text: "The goal is not to eliminate healthy academic ambition, but to ensure that students understand that growth matters too." },
    ],
  },
  {
    n: 5,
    title: "Build Confidence Through Small Successes 🌱",
    blocks: [
      { type: "p", text: "Confidence develops when learners experience progress and discover that they are capable of overcoming challenges." },
      { type: "p", text: "Teachers and parents can support this by breaking difficult tasks into manageable steps." },
      {
        type: "example",
        text: "Large goal: Give a presentation.",
        list: [
          "Choose a topic.",
          "Research information.",
          "Prepare three main points.",
          "Practice speaking.",
          "Present to a small group.",
          "Reflect on what went well.",
          "Try again.",
        ],
        listLabel: "Smaller steps:",
      },
      { type: "p", text: "Small achievements can gradually build the confidence needed to handle bigger challenges." },
      { type: "key", text: "Give learners opportunities to say, “I couldn't do this before, but now I can.”" },
    ],
  },
  {
    n: 6,
    title: "Teach Life Skills Alongside Academic Skills 📚",
    blocks: [
      { type: "p", text: "Education should prepare learners not only for examinations but also for everyday life." },
      { type: "p", text: "Important life skills include:" },
      {
        type: "list",
        items: [
          "Communication",
          "Critical thinking",
          "Problem-solving",
          "Time management",
          "Teamwork",
          "Decision-making",
          "Financial awareness",
          "Digital responsibility",
          "Emotional awareness",
          "Adaptability",
        ],
      },
      { type: "p", text: "These skills can be developed through practical activities." },
      { type: "example", text: "For example, students can work together to plan a small event, create a budget, solve a community problem, or present a project." },
      { type: "p", text: "Academic knowledge becomes more meaningful when learners understand how it can be applied in real situations." },
    ],
  },
  {
    n: 7,
    title: "Encourage Creativity and Different Ways of Learning 🎨",
    blocks: [
      { type: "p", text: "Every learner does not learn in exactly the same way." },
      { type: "p", text: "Some students may enjoy:" },
      {
        type: "list",
        items: [
          "Reading",
          "Drawing",
          "Writing",
          "Speaking",
          "Building",
          "Demonstrating",
          "Experimenting",
          "Working in groups",
          "Working independently",
        ],
      },
      { type: "p", text: "Whenever possible, educators can provide different ways for students to explore and demonstrate their understanding." },
      {
        type: "example",
        text: "For example, after learning about an environmental issue, students might:",
        list: [
          "Write an article",
          "Create a poster",
          "Give a presentation",
          "Make a model",
          "Conduct a small survey",
          "Develop an awareness campaign",
        ],
      },
      { type: "p", text: "This can make learning more engaging while allowing students to use different strengths." },
    ],
  },
  {
    n: 8,
    title: "Make Kindness and Respect Part of Education 🤝",
    blocks: [
      { type: "p", text: "Education also shapes how people treat one another." },
      { type: "p", text: "Schools, families, and communities can intentionally encourage:" },
      {
        type: "list",
        items: [
          "Respect",
          "Empathy",
          "Cooperation",
          "Inclusion",
          "Responsibility",
          "Listening",
          "Helping others",
        ],
      },
      { type: "p", text: "Simple practices can make a difference:" },
      {
        type: "list",
        items: [
          "Listen before judging.",
          "Respect differences.",
          "Help someone who is struggling.",
          "Say thank you.",
          "Apologize when appropriate.",
          "Include others in activities.",
        ],
      },
      { type: "p", text: "When learners understand that success is not only about individual achievement but also about how we contribute to others, education can have a wider social impact." },
    ],
  },
  {
    n: 9,
    title: "Support Healthy Learning Habits 🧘",
    blocks: [
      { type: "p", text: "Learning is affected by overall well-being. Students need appropriate opportunities for rest, movement, recreation, social connection, and focused study." },
      { type: "p", text: "Families and educators can encourage learners to develop healthy routines around:" },
      {
        type: "list",
        items: [
          "Sleep",
          "Physical activity",
          "Nutritious meals",
          "Study time",
          "Breaks",
          "Recreation",
          "Social interaction",
          "Responsible technology use",
        ],
      },
      { type: "routine", text: "Focus → Short break → Review → Practice → Rest", label: "A practical study routine might include:" },
      { type: "p", text: "The aim is to create sustainable learning habits rather than expecting students to study continuously for long periods." },
    ],
  },
  {
    n: 10,
    title: "Connect Education With Real Life and the Community 🌍",
    blocks: [
      { type: "p", text: "Learning becomes particularly meaningful when students can see how it connects to the world around them." },
      { type: "p", text: "Educators can connect lessons with:" },
      {
        type: "list",
        items: [
          "Local community issues",
          "Environmental responsibility",
          "Culture and traditions",
          "Careers",
          "Entrepreneurship",
          "Public service",
          "Everyday decision-making",
          "Real-world problems",
        ],
      },
      { type: "example", text: "For example, students learning mathematics could study household budgeting. Students learning science could investigate local environmental questions. Students learning language could interview community members and write their stories." },
      { type: "p", text: "This approach helps learners understand that education is not limited to a classroom." },
    ],
  },
];

const Block = ({ block }) => {
  if (block.type === "p")
    return <p className="text-base leading-[1.85] text-nh-body md:text-lg">{block.text}</p>;
  if (block.type === "list")
    return (
      <ul className="grid gap-2.5 sm:grid-cols-2">
        {block.items.map((item) => (
          <li key={item} className="flex items-start gap-3 text-[15px] leading-relaxed text-nh-body md:text-base">
            <Leaf className="mt-1.5 h-3.5 w-3.5 shrink-0 text-nh-green" strokeWidth={2.5} />
            {item}
          </li>
        ))}
      </ul>
    );
  if (block.type === "example")
    return (
      <div className="rounded-2xl border-l-4 border-nh-gold bg-amber-50/70 p-6 md:p-7" data-testid="article-example-box">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-nh-gold">Example</p>
        <p className="mt-3 text-[15px] leading-[1.8] text-nh-body md:text-base">{block.text}</p>
        {block.listLabel && (
          <p className="mt-4 text-sm font-semibold text-nh-ink">{block.listLabel}</p>
        )}
        {block.list && (
          <ol className="mt-2 list-decimal space-y-1.5 pl-5 text-[15px] leading-[1.8] text-nh-body md:text-base">
            {block.list.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ol>
        )}
      </div>
    );
  if (block.type === "key")
    return (
      <div className="rounded-2xl bg-nh-mint p-6 md:p-7" data-testid="article-key-message">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-nh-green">
          {block.label || "Key message"}
        </p>
        <p className="mt-3 font-heading text-lg font-semibold italic leading-relaxed text-nh-deep md:text-xl">
          {block.text}
        </p>
      </div>
    );
  if (block.type === "routine")
    return (
      <div className="rounded-2xl bg-nh-mint p-6 md:p-7" data-testid="article-routine-box">
        <p className="text-[15px] text-nh-body md:text-base">{block.label}</p>
        <p className="mt-3 font-heading text-lg font-semibold text-nh-deep md:text-xl">{block.text}</p>
      </div>
    );
  return null;
};

export default function Article() {
  return (
    <>
      <SEO
        title="10 Tips for Better Education | Nurturing Happiness"
        description="Education is not only about examinations and grades. Discover 10 practical tips that help learners grow with confidence, curiosity and happiness."
      />

      <article data-testid="article-page">
        <header className="paper-texture py-16 md:py-24">
          <div className="mx-auto max-w-3xl px-6">
            <Reveal>
              <Link
                to="/what-we-do"
                data-testid="article-back-link"
                className="inline-flex items-center gap-2 text-sm font-semibold text-nh-green transition-colors hover:text-nh-deep"
              >
                <ArrowLeft className="h-4 w-4" /> What We Do
              </Link>
              <h1 className="mt-6 font-heading text-3xl font-bold leading-tight tracking-tight text-nh-ink sm:text-5xl">
                🌱 10 Tips for Better Education
              </h1>
              <p className="mt-4 text-xs font-semibold uppercase tracking-[0.25em] text-nh-green">
                Nurturing Happiness — Education
              </p>
            </Reveal>
          </div>
        </header>

        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <img
              src="https://images.pexels.com/photos/29555738/pexels-photo-29555738.jpeg"
              alt="Schoolchildren learning together outdoors with their teachers"
              loading="lazy"
              className="w-full rounded-3xl object-cover shadow-soft md:aspect-[2/1]"
            />
          </Reveal>

          <Reveal className="mt-10 space-y-5">
            <p className="text-base leading-[1.85] text-nh-body md:text-lg">
              Education is not only about examinations, grades, and academic achievement. True
              education helps children and young people develop knowledge, confidence, curiosity,
              relationships, values, and the ability to live meaningful and fulfilling lives.
            </p>
            <p className="text-base leading-[1.85] text-nh-body md:text-lg">
              At Nurturing Happiness, we believe that learning becomes more powerful when learners
              feel supported, respected, motivated, and happy.
            </p>
          </Reveal>
        </div>

        <div className="mx-auto max-w-3xl px-6 py-14 md:py-20">
          {SECTIONS.map((section) => (
            <Reveal key={section.n}>
              <section data-testid={`article-section-${section.n}`} className="relative pb-14 md:pb-16">
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute -top-6 right-0 select-none font-heading text-7xl font-bold text-nh-fresh/20 md:-top-9 md:text-9xl"
                >
                  {section.n}
                </span>
                <h2 className="relative max-w-xl pr-14 font-heading text-2xl font-bold leading-snug text-nh-deep sm:pr-0 sm:text-3xl">
                  <span className="mr-3 text-nh-gold">{section.n}.</span>
                  {section.title}
                </h2>
                <div className="mt-6 space-y-5 md:space-y-6">
                  {section.blocks.map((block, i) => (
                    <Block key={i} block={block} />
                  ))}
                </div>
                {section.n < SECTIONS.length && (
                  <div aria-hidden="true" className="mt-14 flex items-center justify-center gap-4 md:mt-16">
                    <span className="h-px w-16 bg-nh-deep/10" />
                    <Leaf className="h-4 w-4 text-nh-green/50" strokeWidth={2} />
                    <span className="h-px w-16 bg-nh-deep/10" />
                  </div>
                )}
              </section>
            </Reveal>
          ))}

          <Reveal className="mt-2 md:mt-4">
            <section data-testid="article-bigger-picture" className="rounded-[2rem] bg-nh-deep p-7 text-white shadow-lift sm:p-10 md:p-12">
              <h2 className="font-heading text-2xl font-bold sm:text-3xl">🌈 The Bigger Picture</h2>
              <p className="mt-5 text-base leading-[1.85] text-nh-cream/85">
                The purpose of education is broader than achieving high marks.
              </p>
              <p className="mt-4 text-base leading-[1.85] text-nh-cream/85">
                A strong educational environment can help learners become:
              </p>
              <ul className="mt-5 space-y-3">
                {[
                  "Curious learners who keep asking questions.",
                  "Confident individuals who believe they can improve.",
                  "Critical thinkers who examine information carefully.",
                  "Kind people who respect others.",
                  "Responsible citizens who contribute to their communities.",
                  "Lifelong learners who continue growing beyond school.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-[15px] leading-relaxed text-nh-cream/85 md:text-base">
                    <Leaf className="mt-1.5 h-3.5 w-3.5 shrink-0 text-nh-yellow" strokeWidth={2.5} />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-base leading-[1.85] text-nh-cream/85">
                At Nurturing Happiness, our vision is to connect education, personal growth,
                well-being, and community so that learning contributes not only to academic
                development but also to a more meaningful and fulfilling life.
              </p>
            </section>
          </Reveal>

          <Reveal className="mt-10 md:mt-12">
            <section data-testid="article-closing" className="rounded-[2rem] border border-nh-gold/30 bg-amber-50/70 p-7 text-center sm:p-10 md:p-12">
              <div className="space-y-2 font-heading text-xl font-semibold text-nh-deep md:text-2xl">
                <p>💚 Nurture the mind.</p>
                <p>🌱 Inspire the heart.</p>
                <p>📚 Empower the learner.</p>
                <p>🌍 Build happier communities.</p>
              </div>
              <p className="mt-7 text-sm font-semibold uppercase tracking-[0.18em] text-nh-green">
                Nurturing Happiness — Better Learning, Brighter Futures, Happier Communities.
              </p>
              <Link
                to="/contact"
                data-testid="article-contact-cta"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-nh-green px-8 py-3.5 text-[15px] font-semibold text-white shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:bg-nh-deep hover:shadow-lift active:scale-95"
              >
                Get in Touch <ArrowRight className="h-4 w-4" />
              </Link>
            </section>
          </Reveal>
        </div>
      </article>
    </>
  );
}
